import * as React from 'react';
import { cn } from '../../../utils';

export interface WorkbookPanelProps {
  /** rotated red tab text, e.g. "Workbook · auto-checked" */
  tab: React.ReactNode;
  /** condensed uppercase question/title */
  title?: React.ReactNode;
  /** small hint line, e.g. "Choose one answer." */
  hint?: React.ReactNode;
  /** exercise body */
  children?: React.ReactNode;
  /** footer slot: buttons + attempt text (laid out in a wrapping flex row) */
  footer?: React.ReactNode;
  className?: string;
}

/**
 * Dashed red workbook panel on cream paper with a rotated red tab.
 */
export function WorkbookPanel({ tab, title, hint, children, footer, className }: WorkbookPanelProps) {
  return (
    <div className={cn('z-workbook', className)}>
      <span className="z-wb-tab">{tab}</span>
      {title != null && <h3 className="z-wb-title">{title}</h3>}
      {hint != null && <p className="z-hint">{hint}</p>}
      {children}
      {footer != null && <div className="z-wb-foot">{footer}</div>}
    </div>
  );
}

export interface VerdictProps {
  /** the verdict box is hidden until `show` is true */
  show?: boolean;
  /** 'good' = green success box (default); 'bad' = red/baby error tint */
  tone?: 'good' | 'bad';
  children?: React.ReactNode;
  className?: string;
}

/** Verdict box shown after checking answers ("Correct — … +10 EP"). */
export function Verdict({ show = false, tone = 'good', children, className }: VerdictProps) {
  return (
    <div
      className={cn('z-verdict', show && 'z-show', className)}
      role="status"
      style={
        tone === 'bad'
          ? { background: '#F8D7DD', borderColor: '#E8A7B6', color: '#CF2E2E' }
          : undefined
      }
    >
      {children}
    </div>
  );
}

export interface AttemptTextProps {
  children?: React.ReactNode;
  className?: string;
}

/** Muted attempt counter for the workbook footer, e.g. "First attempt". */
export function AttemptText({ children, className }: AttemptTextProps) {
  return <span className={cn('z-attempt', className)}>{children}</span>;
}

export interface ZineButtonProps extends React.ButtonHTMLAttributes<HTMLButtonElement> {
  /** 'primary' = red-to-pink gradient pill; 'dark' = ink pill */
  variant?: 'primary' | 'dark';
  /**
   * An action is in flight (e.g. the answer is being graded): the button is
   * disabled and shows a spinner before its label, like the core Button's
   * `isLoading`. Pair it with the "Submitting…" label.
   */
  isLoading?: boolean;
}

/** Gradient pill button (spec `.btn`), with the ink `dark` variant. */
export const ZineButton = React.forwardRef<HTMLButtonElement, ZineButtonProps>(
  function ZineButton(
    { variant = 'primary', className, type = 'button', isLoading = false, disabled, children, ...rest },
    ref,
  ) {
    return (
      <button
        ref={ref}
        type={type}
        className={cn(
          'z-btn',
          variant === 'dark' && 'z-btn--dark',
          isLoading && 'z-btn--loading',
          className,
        )}
        disabled={disabled || isLoading}
        aria-busy={isLoading || undefined}
        {...rest}
      >
        {isLoading ? <span className="z-spinner" aria-hidden="true" /> : null}
        {children}
      </button>
    );
  },
);

export interface OptionGroupProps {
  children?: React.ReactNode;
  className?: string;
}

/** Vertical stack for OptionRows (spec `.opts` grid). */
export function OptionGroup({ children, className }: OptionGroupProps) {
  return <div className={cn('z-opts', className)}>{children}</div>;
}

export interface OptionRowProps extends React.ButtonHTMLAttributes<HTMLButtonElement> {
  /** the A/B/C key badge */
  keyLabel: React.ReactNode;
  /** 'picked' = red selection, 'right' = green correct state */
  state?: 'default' | 'picked' | 'right';
}

/**
 * Multiple-choice option row: key badge + label, with picked (red) and
 * right (green) states. Presentational/controlled — the caller owns state.
 */
export const OptionRow = React.forwardRef<HTMLButtonElement, OptionRowProps>(
  function OptionRow(
    { keyLabel, state = 'default', className, type = 'button', children, ...rest },
    ref,
  ) {
    return (
      <button
        ref={ref}
        type={type}
        className={cn(
          'z-opt',
          state === 'picked' && 'z-opt--picked',
          state === 'right' && 'z-opt--right',
          className,
        )}
        aria-pressed={state === 'picked' || undefined}
        {...rest}
      >
        <span className="z-key">{keyLabel}</span>
        {children}
      </button>
    );
  },
);

export interface BlankProps extends Omit<React.InputHTMLAttributes<HTMLInputElement>, 'type'> {
  /** 'right' tints the blank green; 'wrong' deep red — default is charm pink */
  state?: 'default' | 'right' | 'wrong';
  /** override the 8ch default width */
  widthCh?: number;
}

/**
 * Underlined inline fill-in-the-blank input (spec `.blank`).
 * Controlled via standard value/onChange props.
 */
export const Blank = React.forwardRef<HTMLInputElement, BlankProps>(
  function Blank({ state = 'default', widthCh, className, style, ...rest }, ref) {
    return (
      <input
        ref={ref}
        type="text"
        className={cn(
          'z-blank',
          state === 'right' && 'z-blank--right',
          state === 'wrong' && 'z-blank--wrong',
          className,
        )}
        style={widthCh != null ? { width: `${widthCh}ch`, ...style } : style}
        {...rest}
      />
    );
  },
);
